import * as yup from 'yup'

import {
  IEgdApiClient,
  AdapterCollection,
  AccountSettingsForm,
  DomainSettingsForm,
  RadioButtonStatesAdapter,
  settings,
  CustomScheduleAdapter,
  StringAdapter,
  CSVAdapter,
  schemas,
  validateInContext,
  IAuth
} from 'v2/modules/lib'

export interface INotificationsFormArgs {
  egdApiClient: IEgdApiClient
  accountId: string
  domainId: number | null
  allowedDomainNames: string[]
  auth: IAuth
}

export const NotificationsForm = ({
  egdApiClient,
  accountId,
  domainId,
  allowedDomainNames,
  auth
}: INotificationsFormArgs) => {
  const schema = yup.object().shape({
    // emails
    [settings.outbound_email_ids.inputName]: yup
      .string()
      .typeError('field_errors.invalid_value')
      .test({
        name: 'is_valid_outbound_ids',
        test: (value, ctx) => {
          if (['custom', 'immediately'].includes(ctx.parent[settings.outbound_quarantine_interval.inputName])) {
            const check = validateInContext(settings.outbound_email_ids.schema, ctx)(value)
            return check.result || check.error
          }
          return true
        }
      }),
    [settings.sender_notification_address.inputName]: yup
      .string()
      .typeError('field_errors.invalid_value')
      .test({
        name: 'is_valid_sender_notification_address',
        test: (value, ctx) => {
          if (ctx.parent[settings.sender_notification_enable.inputName] === 'on') {
            const emailSchema = schemas.required_email_on_allowed_domain(allowedDomainNames)
            const check = validateInContext(emailSchema, ctx)(value)
            return check.result || check.error
          }
          return true
        }
      }),
    [settings.reject_notification_address.inputName]: schemas.optional_email_on_allowed_domain(allowedDomainNames),
    [settings.outbound_spam_notification_address.inputName]: settings.outbound_spam_notification_address.schema,
    // not emails
    [settings.outbound_quarantine_interval.inputName]: yup
      .string()
      .typeError('field_errors.invalid_value')
      .test({
        name: 'is_valid_outbound_quarantine_interval',
        test: (value, ctx) => {
          // Outbound email IDs is empty -> only never allowed
          if (!ctx.parent[settings.outbound_email_ids.inputName]?.trim()) {
            if (value !== 'never') {
              return ctx.createError({ message: 'field_errors.outbound_email_ids_required' })
            }
          }

          // Outbound email IDs not empty, validate against schema
          const check = validateInContext(settings.outbound_quarantine_interval.schema, ctx)(value)
          return check.result || check.error
        }
      }),
    [settings.custom_outbound_schedule.inputName]: settings.custom_outbound_schedule.schema,
    [settings.sender_notification_enable.inputName]: settings.sender_notification_enable.schema,
    [settings.sender_notification_subject.inputName]: settings.sender_notification_subject.schema,
    [settings.sender_notification_template.inputName]: settings.sender_notification_template.schema,
    [settings.reject_notification_subject.inputName]: settings.reject_notification_subject.schema,
    [settings.reject_notification_template.inputName]: settings.reject_notification_template.schema
  })

  const adapters = AdapterCollection()
  adapters.add(StringAdapter(settings.outbound_quarantine_interval))
  adapters.add(CustomScheduleAdapter(settings.custom_outbound_schedule))
  adapters.add(CSVAdapter(settings.outbound_email_ids))
  adapters.add(RadioButtonStatesAdapter(settings.sender_notification_enable))
  adapters.add(StringAdapter(settings.sender_notification_address))
  adapters.add(StringAdapter(settings.sender_notification_subject))
  adapters.add(StringAdapter(settings.sender_notification_template))
  adapters.add(StringAdapter(settings.reject_notification_address))
  adapters.add(StringAdapter(settings.reject_notification_subject))
  adapters.add(StringAdapter(settings.reject_notification_template))
  adapters.add(StringAdapter(settings.outbound_spam_notification_address))

  if (domainId) {
    return {
      form: DomainSettingsForm({
        egdApiClient,
        accountId,
        domainId,
        adapters,
        auth
      }),
      schema
    }
  }
  return {
    form: AccountSettingsForm({
      egdApiClient,
      accountId,
      adapters,
      auth
    }),
    schema
  }
}

export type INotificationsForm = ReturnType<typeof NotificationsForm>
